import { getMyLocations } from './locationApi';
import { getWorkoutPlans } from './workoutPlanApi';
import { getWorkoutSessions } from './workoutSessionApi';
import { searchExerciseDefinitions } from './exerciseDefinitionApi';
import { getLocationFeatures } from './locationFeatureApi';
import { getBioLogs } from './bioLogsApi'; // Added

/**
 * Fetches the complete resource map from the backend.
 * OPTIMIZATION: We use Promise.allSettled to ensure that if one fails,
 * the others still load, preventing a blank screen.
 *
 * UPDATED: Now uses sparse fieldsets for heavy resources to improve initial load time.
 * Objects are hydrated on-demand by components using useResourceHydration.
 */
export async function fetchAllResources() {
  console.log('[WorkoutSmart/resourceApi] Fetching user resources (Lazy loading catalogs)...');

  const sessionFields = 'id,name,status,start_time,end_time,created_at';
  const planFields = 'id,name,description,created_at,updated_at,chat_session_id';
  const activityFields = 'id,name,taxonomy.modalities,created_at,popularity';
  const equipmentFields = 'id,name';

  const results = await Promise.allSettled([
    getMyLocations(),
    getWorkoutPlans({ fields: planFields }),
    getWorkoutSessions({ fields: sessionFields }),
    searchExerciseDefinitions({ scope: 'personalized', fields: activityFields }),
    getLocationFeatures({ scope: 'personalized', fields: equipmentFields }),
    getBioLogs() // Added
  ]);

  const [locations, workoutPlans, workoutSessions, activities, equipment, bioLogs] = results;

  const resourceMap = {
    location: locations.status === 'fulfilled' ? locations.value : [],
    equipment: equipment.status === 'fulfilled' ? equipment.value : [],
    activity: activities.status === 'fulfilled' ? activities.value : [],
    workout_plan: workoutPlans.status === 'fulfilled' ? workoutPlans.value : [],
    workout_session: workoutSessions.status === 'fulfilled' ? workoutSessions.value : [],
    bio_log: bioLogs.status === 'fulfilled' ? bioLogs.value : [], // Added
  };

  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      const keys = ['location', 'workout_plan', 'workout_session', 'exercise_definition', 'equipment', 'bio_log'];
      console.error(`[WorkoutSmart/resourceApi] Failed to fetch ${keys[index]}:`, result.reason);
    }
  });

  console.log('[WorkoutSmart/resourceApi] Finished fetching user resources.');
  return resourceMap;
}

export function processAllResources(resourceMap, bookmarksMetadata = []) {
  if (!resourceMap) return [];

  const bookmarkSet = new Set(
    bookmarksMetadata.filter(m => m.metadata?.bookmarked).map(m => m.resource_id)
  );

  const process = (items, type) => {
    if (!Array.isArray(items)) return [];
    return items.map(item => ({
      ...item,
      resource_type: type,
      type: type,
      isBookmarked: bookmarkSet.has(item.id),
      _isPartial: item._isPartial || false
    }));
  };

  const allResources = [
    ...process(resourceMap.location, 'location'),
    ...process(resourceMap.equipment, 'equipment'),
    ...process(resourceMap.activity, 'exercise_definition'),
    ...process(resourceMap.workout_plan, 'workout_plan'),
    ...process(resourceMap.workout_session, 'workout_session'),
    ...process(resourceMap.bio_log, 'bio_log'),
  ];

  return allResources.filter(Boolean);
}